import { MapPin, Phone, Mail, MessagesSquare, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useSiteImages } from "@/lib/siteImages";
import { useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";

const MAP_IMG =
  "https://images.unsplash.com/photo-1733195296321-b99d129b09cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjB3b3JsZCUyMG1hcCUyMGFic3RyYWN0JTIwbm9kZXN8ZW58MHx8fHwxNzg4MjI0MTE1fDA&ixlib=rb-4.1.0&q=85";

export const Contact = () => {
  const si = useSiteImages();
  const { t } = useLang();
  return (
  <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-surface">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
      <Reveal>
        <Eyebrow>{t.contact.eyebrow}</Eyebrow>
        <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] mb-10">
          {t.contact.title}
        </h2>

        <div className="space-y-6 mb-10">
          <div className="flex gap-4">
            <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] mb-1">{t.contact.officeLabel}</p>
              <p className="text-base text-[#F4F6F8]" data-testid="contact-address">
                Plots No. 3088, CCC Ark of Covenant, Lugbe, Abuja, Nigeria.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <Phone className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] mb-1">{t.contact.phoneLabel}</p>
              <a href="tel:+2349068043369" data-testid="contact-phone" className="text-base text-[#F4F6F8] hover:text-gold transition-colors duration-200">
                +234 906 804 3369
              </a>
            </div>
          </div>
          <div className="flex gap-4">
            <Mail className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] mb-1">{t.contact.emailLabel}</p>
              <a href="mailto:norexinternationaltradingltd@gmail.com" data-testid="contact-email" className="text-base text-[#F4F6F8] hover:text-gold transition-colors duration-200 break-all">
                norexinternationaltradingltd@gmail.com
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <a
            data-testid="contact-email-btn"
            href="mailto:norexinternationaltradingltd@gmail.com"
            className="inline-flex items-center gap-2 bg-gold text-midnight font-semibold px-6 py-3.5 rounded-full hover:bg-gold-light hover:text-midnight transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
          >
            <Mail className="w-4 h-4" /> {t.contact.emailBtn}
          </a>
          <a
            data-testid="contact-whatsapp-btn"
            href="https://wa.me/2349068043369"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-white/30 text-[#F4F6F8] font-semibold px-6 py-3.5 rounded-full hover:border-gold hover:text-gold transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5"
          >
            <MessagesSquare className="w-4 h-4" /> {t.contact.whatsappBtn}
          </a>
          <button
            data-testid="contact-rfq-btn"
            onClick={() => scrollToId("rfq")}
            className="inline-flex items-center gap-2 border border-white/30 text-[#F4F6F8] font-semibold px-6 py-3.5 rounded-full hover:border-gold hover:text-gold transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5"
          >
            {t.contact.quoteBtn} <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="relative h-full min-h-[380px] border border-[#1E293B] overflow-hidden group">
          <img
            src={si("contact", MAP_IMG)}
            alt="Abstract global network of trade nodes"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-[#060A14]/70" />
          <div className="relative h-full flex flex-col justify-end p-10">
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">{t.contact.cardEyebrow}</p>
            <p className="text-2xl md:text-3xl font-display font-bold tracking-tight text-[#F4F6F8] max-w-sm">
              {t.contact.cardTitle}
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
